import { useState } from 'react';
import { BrowserRouter } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

import { ApiClientError } from '@/lib/api';
import { ErrorBoundary } from '@/components/error-boundary';
import { TooltipProvider } from '@/components/ui/tooltip';
import { Toaster } from '@/components/ui/toast';
import { AppRoutes } from '@/router';

/**
 * QueryClient defaults.
 *
 * The mutation policy is the important half and it is not negotiable:
 * `retry: false` on every mutation, everywhere, for the reason spelled out in
 * lib/api.ts — a retried dose log is a double-dose risk, and a request that
 * timed out may well have succeeded. Server-side idempotency is the safety net,
 * not the plan.
 *
 * Reads get exactly one retry, which covers a dropped packet on flaky LAN wifi
 * without turning a genuine 500 into a four-second stall. A 4xx is never
 * retried: it is an answer, not a hiccup.
 */
export function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        // Long enough that switching tabs does not refetch the world; short
        // enough that a dose logged on a phone shows up on the tablet quickly.
        staleTime: 30_000,
        gcTime: 5 * 60_000,
        retry: (failureCount, error) => {
          if (error instanceof ApiClientError) {
            // Nothing the client can fix by asking again.
            if (error.status >= 400 && error.status < 500) return false;
          }
          return failureCount < 1;
        },
        refetchOnWindowFocus: true,
        refetchOnReconnect: true,
      },
      mutations: {
        // See above. Do not change this, and do not override it per-mutation.
        retry: false,
      },
    },
  });
}

export function App(): JSX.Element {
  // One client for the life of the app, created inside the component so React
  // Strict Mode's double-invoke does not build two of them.
  const [queryClient] = useState(createQueryClient);

  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <TooltipProvider>
          <BrowserRouter>
            <AppRoutes />
          </BrowserRouter>
          {/* Outside the router on purpose: a toast about a failed dose log
              must survive the navigation that follows it. */}
          <Toaster />
        </TooltipProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}

export default App;
